import { useState } from "react";
import { useAuth } from "../context/AuthContext";
import { api, formatApiError } from "../lib/api";
import { ShieldAlert, Trash2, RotateCcw } from "lucide-react";

export function AccountDeletion() {
  const { user, refreshUser } = useAuth();
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const purgeAt = user?.deletion_purge_at;

  const requestDeletion = async () => {
    setBusy(true); setError("");
    try { await api.post("/account/request-deletion"); await refreshUser(); setOpen(false); }
    catch (err) { setError(formatApiError(err.response?.data?.detail) || err.message); }
    finally { setBusy(false); }
  };
  const cancelDeletion = async () => {
    setBusy(true); setError("");
    try { await api.post("/account/cancel-deletion"); await refreshUser(); }
    catch (err) { setError(formatApiError(err.response?.data?.detail) || err.message); }
    finally { setBusy(false); }
  };

  if (purgeAt) {
    return (
      <div className="mt-10 bg-red-50 border border-red-200 rounded-2xl p-6" data-testid="account-deletion-scheduled">
        <h3 className="font-heading font-bold text-lg text-red-900 flex items-center gap-2"><ShieldAlert className="w-5 h-5" /> Account scheduled for deletion</h3>
        <p className="mt-1 text-sm text-red-800">
          Your account and personal data will be permanently deleted on{" "}
          <span className="font-semibold">{new Date(purgeAt).toLocaleDateString("en-GB")}</span>.
          Your published reviews contain no personal data and will stay live. Change your mind? You can restore your account any time before then.
        </p>
        {error && <p className="mt-2 text-sm text-red-700" data-testid="account-deletion-error">{error}</p>}
        <button data-testid="cancel-deletion-btn" onClick={cancelDeletion} disabled={busy}
          className="mt-4 inline-flex items-center gap-2 bg-navy text-white font-semibold px-5 py-2.5 rounded-xl hover:bg-navy-600 transition-colors disabled:opacity-60">
          <RotateCcw className="w-4 h-4" /> {busy ? "Restoring…" : "Keep my account"}
        </button>
      </div>
    );
  }

  return (
    <div className="mt-10 bg-white border border-slate-200 rounded-2xl p-6" data-testid="account-deletion">
      <h3 className="font-heading font-bold text-lg text-navy">Delete my account</h3>
      <p className="mt-1 text-sm text-slate-500">Permanently close your Tenverly account and erase your personal data.</p>
      {!open ? (
        <button data-testid="open-delete-account" onClick={() => setOpen(true)}
          className="mt-4 inline-flex items-center gap-2 border border-red-300 text-red-700 font-semibold px-5 py-2.5 rounded-xl hover:bg-red-50 transition-colors">
          <Trash2 className="w-4 h-4" /> Delete my account
        </button>
      ) : (
        <div className="mt-4 bg-red-50 border border-red-200 rounded-xl p-4" data-testid="delete-account-confirm">
          <p className="text-sm text-red-800">
            You'll have <span className="font-semibold">14 days</span> to change your mind — just log back in and click
            "Keep my account". After that your account and personal data are permanently erased.
          </p>
          <p className="mt-2 text-sm text-red-800">
            Your published reviews do <span className="font-semibold">not</span> contain any personal or GDPR data, so they stay
            live to help other renters. If you'd like a specific review removed, delete it individually from your reviews list above.
          </p>
          {error && <p className="mt-2 text-sm text-red-700" data-testid="account-deletion-error">{error}</p>}
          <div className="mt-4 flex gap-2">
            <button data-testid="confirm-delete-account" onClick={requestDeletion} disabled={busy}
              className="inline-flex items-center gap-2 bg-red-600 text-white font-semibold px-5 py-2.5 rounded-xl hover:bg-red-700 transition-colors disabled:opacity-60">
              <Trash2 className="w-4 h-4" /> {busy ? "Scheduling…" : "Yes, delete my account"}
            </button>
            <button onClick={() => setOpen(false)} className="text-sm font-semibold text-slate-500 px-3">Cancel</button>
          </div>
        </div>
      )}
    </div>
  );
}
